import { useRef, useMemo } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';

import { dataGridPremiumColumns } from '../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  AutoCompleteSelectUI,
  DatagridPremiumUi,
  InputUI,
  CheckboxUI,
} from '../Interface';
import useConfig from '../../context/ConfigContext';
import useWindowSubtractedHeight from '../../hooks/useWindowSubtractedHeight';

const EditToolbar = (props) => {
  const { disabled, push, defaultServiceMaterial } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    defaultServiceMaterial.id = uuidv4();
    defaultServiceMaterial.isNew = true;
    defaultServiceMaterial.qtyUnitId = null;
    await push(defaultServiceMaterial);

    await validateField(`serviceMaterial.${values.serviceMaterial.length ?? 0}.itemCode`);
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const ServiceMaterial = ({ disabled, defaultServiceMaterial, autocompleteOptions, autocompleteData }) => {
  const { config } = useConfig();
  const focusedFieldRef = useRef(null);
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const gridHeight = useWindowSubtractedHeight({ otherContentHeight: 600, minHeight: 480 });

  const getValueIndex = (values, id) => {
    return values.serviceMaterial.findIndex((rc) => rc.id === id);
  };

  const columns = useMemo(
    () => (remove) =>
      dataGridPremiumColumns([
        {
          field: 'itemCode',
          headerName: 'Cikk',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions.items.find(
              (u) => u.value === values.serviceMaterial?.[valueIndex]?.itemCode
            );
            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions.items.find(
              (u) => u.value === values.serviceMaterial?.[valueIndex]?.itemCode
            );
            return (
              <Field
                name={`serviceMaterial.${valueIndex}.itemCode`}
                type="text"
                variant="standard"
                as={AutoCompleteSelectUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.serviceMaterial?.[valueIndex]?.itemCode} />}
                onChange={(_e, newVal) => {
                  const val = newVal?.value ?? null;
                  const findedI = autocompleteData.items.find((i) => i.itemCode === val);

                  setFieldValue(`serviceMaterial.${valueIndex}.itemCode`, val);
                  setFieldValue(`serviceMaterial.${valueIndex}.itemName`, findedI?.itemName ?? '');
                  setFieldValue(`serviceMaterial.${valueIndex}.qtyUnitId`, findedI?.quantityUnitId ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: val,
                  });
                }}
                onBlur={async () => await validateField(`serviceMaterial.${valueIndex}.itemCode`)}
                value={findedItem}
                isOptionEqualToValue={(option, value) => option.value === value}
                dataset={autocompleteOptions.items}
              />
            );
          },
        },

        {
          field: 'qty',
          headerName: 'Mennyiség',
          type: 'string',
          editable: true,
          sortable: false,
          valueGetter: (_data, row) => {
            const valueIndex = getValueIndex(values, row.id);
            return values?.serviceMaterial?.[valueIndex]?.qty;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`serviceMaterial.${valueIndex}.qty`}
                type="number"
                as={InputUI}
                variant="standard"
                fullWidth
                autoFocus={focusedFieldRef.current === 'qty'}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
                helperText={<FormErrorUI message={errors?.serviceMaterial?.[valueIndex]?.qty} />}
                onChange={(e) => {
                  setFieldValue(`serviceMaterial.${valueIndex}.qty`, e.target?.value);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target?.value,
                  });
                }}
                onBlur={async () => await validateField(`serviceMaterial.${valueIndex}.qty`)}
              />
            );
          },
        },

        {
          field: 'qtyUnitId',
          headerName: 'Me.',
          type: 'string',
          filterable: false,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            const findedUnit = autocompleteData.quantityUnit.find(
              (u) => u.id === values.serviceMaterial?.[valueIndex]?.qtyUnitId
            );
            return findedUnit?.code ?? '';
          },
        },

        {
          field: 'isRequired',
          headerName: 'Kötelező elem',
          type: 'boolean',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`serviceMaterial.${valueIndex}.isRequired`}
                type="checkbox"
                component={CheckboxUI}
                helperText={<FormErrorUI message={errors?.serviceMaterial?.[valueIndex]?.isRequired} />}
                checked={values.serviceMaterial?.[valueIndex]?.isRequired ?? false}
                variant="standard"
                onChange={(e) => {
                  setFieldValue(`serviceMaterial.${valueIndex}.isRequired`, e.target.checked);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.checked,
                  });
                }}
              />
            );
          },
        },

        {
          field: 'actions',
          type: 'actions',
          headerName: '',
          cellClassName: 'actions',
          sortable: false,
          getActions: ({ id, row }) => {
            return [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const valueIndex = getValueIndex(values, id);
                  return remove(valueIndex);
                }}
                color="inherit"
                disabled={disabled}
              />,
            ];
          },
        },
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      autocompleteOptions.items,
      autocompleteData.items,
      autocompleteData.quantityUnit,
      values.serviceMaterial,
      errors,
      setFieldValue,
      validateField,
      disabled,
    ]
  );

  return (
    <>
      <div className="overflow-x-auto">
        <div className="align-middle">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <FieldArray name="serviceMaterial">
              {({ remove, push }) => (
                <DatagridPremiumUi
                  apiRef={apiRef}
                  datagridIdentifier={DataGridIdentifiers.ServiceMaterial}
                  height={gridHeight}
                  columns={columns(remove)}
                  rows={values?.serviceMaterial ?? []}
                  editMode="row"
                  slots={{
                    toolbar: EditToolbar,
                  }}
                  slotProps={{
                    toolbar: {
                      disabled,
                      push,
                      defaultServiceMaterial,
                    },
                  }}
                  onCellDoubleClick={(_params, event) => {
                    event.defaultMuiPrevented = true;
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  onCellClick={(params, event) => {
                    if (disabled) return;
                    focusedFieldRef.current = params.field;
                    const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

                    for (const rowId of rowsInEditMode) {
                      if (params.row.id.toString() !== rowId) {
                        apiRef.current.stopRowEditMode({ id: rowId });
                      }
                    }

                    const rowMode = apiRef.current.getRowMode(params.row.id);
                    if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                  }}
                  onRowEditStop={() => {
                    focusedFieldRef.current = null;
                  }}
                />
              )}
            </FieldArray>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceMaterial;
